import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../utils/axios';

/**
 * App-wide feature flags (server-backed, from /admin/system/settings).
 *
 * Drives three things from ONE fetch:
 *   - which sidebar tabs render (Sidebar filters by isPathEnabled)
 *   - which routes open (ModuleGuard redirects disabled ones)
 *   - the Settings toggles (Modules + Background Jobs cards)
 *
 * Toggling through `update()` PUTs to the server, merges locally and so the
 * sidebar / routes react instantly without a reload.
 */
const FeatureFlagsContext = createContext(null);

/**
 * Sensitive routes that expose customer PII, messaging, or financial data.
 * If the settings fetch fails we cannot know whether these were toggled off,
 * so we fail CLOSED (hide them) rather than risk leaking a disabled module.
 * Clearly-public/management routes (dashboard, catalogue, geo, blogs, etc.)
 * stay visible so the admin isn't locked out entirely.
 */
const SENSITIVE_PATHS = new Set([
  '/users',
  '/businesses',
  '/customer-enquiry-data',
  '/enquiries',
  '/messages',
  '/whatsapp',
  '/revenue',
  '/reports',
  '/wbr',
  '/subscribers',
]);

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

export const FeatureFlagsProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState({});
  const [crons, setCrons] = useState({});
  const [notifications, setNotifications] = useState({});
  const [moduleCatalogue, setModuleCatalogue] = useState([]);
  const [cronCatalogue, setCronCatalogue] = useState([]);
  const [notificationCatalogue, setNotificationCatalogue] = useState([]);
  const [loadError, setLoadError] = useState(false);

  const refresh = useCallback(async () => {
    // No token yet (e.g. sign-in screen) → don't call, just stop loading.
    if (!localStorage.getItem('admin_token')) { setLoading(false); return; }
    try {
      const { data } = await axios.get('/admin/system/settings', { skipNotify: true });
      setModules(data.settings?.modules_enabled || {});
      setCrons(data.settings?.crons_enabled || {});
      setNotifications(data.settings?.notifications_enabled || {});
      setModuleCatalogue(data.module_catalogue || []);
      setCronCatalogue(data.cron_catalogue || data.catalogue || []);
      setNotificationCatalogue(data.notification_catalogue || []);
      setLoadError(false);
    } catch (e) {
      // Fail CLOSED for sensitive modules: when flags can't load we can't
      // confirm a sensitive module wasn't disabled, so hide those (see
      // SENSITIVE_PATHS / isPathEnabled). Public modules stay visible.
      setModules({});
      setCrons({});
      setNotifications({});
      setLoadError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Persist a partial change and merge into local state immediately.
  const update = useCallback(async (partial) => {
    if (partial.modules_enabled) setModules((m) => ({ ...m, ...partial.modules_enabled }));
    if (partial.crons_enabled) setCrons((c) => ({ ...c, ...partial.crons_enabled }));
    if (partial.notifications_enabled) setNotifications((n) => ({ ...n, ...partial.notifications_enabled }));
    try {
      const { data } = await axios.put('/admin/system/settings', partial, { skipNotify: 'success' });
      if (data.settings) {
        setModules(data.settings.modules_enabled || {});
        setCrons(data.settings.crons_enabled || {});
        setNotifications(data.settings.notifications_enabled || {});
      }
    } catch (e) {
      refresh(); // reconcile on failure
    }
  }, [refresh]);

  // A path is enabled unless it maps to a module flag explicitly set false.
  const isPathEnabled = useCallback((path) => {
    // Settings failed to load → fail closed for sensitive modules only.
    if (loadError && SENSITIVE_PATHS.has(path)) return false;
    const entry = moduleCatalogue.find((m) => m.path === path);
    if (!entry) return true; // not a toggleable module (e.g. /dashboard, /settings)
    return modules[entry.key] !== false;
  }, [moduleCatalogue, modules, loadError]);

  const value = useMemo(() => ({
    loading, modules, crons, notifications, moduleCatalogue, cronCatalogue, notificationCatalogue,
    isPathEnabled, update, refresh,
  }), [loading, modules, crons, notifications, moduleCatalogue, cronCatalogue, notificationCatalogue, isPathEnabled, update, refresh]);

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};
